/**
 * In-browser workout tracker — the Today tab.
 *
 * Loads a workout session (with sets) and lets the user log each set
 * inline. Set-based exercises (strength, plyometric, hold) show a row
 * per set with weight/reps inputs + done/skip + difficulty pill.
 * Single-effort exercises (cardio, mobility) show one "actual" input
 * + done/skip + difficulty.
 *
 * Mirrors the design's WorkoutTracker2 component.
 */

import { useEffect, useMemo, useState, type KeyboardEvent } from 'react';
import { Icon } from '@k-os/ui';
import {
  useWorkoutSession,
  useAddSet,
  usePatchSet,
  useDeleteSet,
  usePatchWorkout,
  usePauseWorkout,
  useResumeWorkout,
  useExercises,
  useEquipment,
  useFavoriteExerciseIds,
  useToggleFavorite,
  type ExerciseDto,
  type ExerciseBlock,
  type ExerciseSetDto,
  type WorkoutSessionDto,
} from '../../../api/health';
import {
  DifficultyPicker,
  EXERCISE_BLOCK_META,
  ExercisePicker,
  SkipReasonModal,
  StatusPill,
  TypeChip,
} from './atoms';
import { ExerciseDetailModal } from '../exercises/HealthExercises';
import { formatActiveElapsed } from './elapsed';
import styles from './workouts.module.css';

const SET_BASED: ReadonlyArray<ExerciseDto['type']> = ['strength', 'plyometric', 'hold'];
const BLOCK_ORDER: ExerciseBlock[] = ['warmup', 'main', 'accessory', 'cooldown'];

export function WorkoutTracker({ sessionId }: { sessionId: string }) {
  const sessionQ = useWorkoutSession(sessionId);
  const exercisesQ = useExercises();
  const equipmentQ = useEquipment();
  const favIdsQ = useFavoriteExerciseIds();
  const toggleFav = useToggleFavorite();
  const addSet = useAddSet();
  const patchSet = usePatchSet();
  const deleteSet = useDeleteSet();
  const patchWorkout = usePatchWorkout();
  const pauseWorkout = usePauseWorkout();
  const resumeWorkout = useResumeWorkout();

  const [editMode, setEditMode] = useState(false);
  const [detailExercise, setDetailExercise] = useState<ExerciseDto | null>(null);
  const [picker, setPicker] = useState<{
    mode: 'add' | 'swap';
    block?: ExerciseBlock | undefined;
    swapExerciseId?: string | undefined;
  } | null>(null);
  const [skipPrompt, setSkipPrompt] = useState<{ exerciseId: string | null; name: string } | null>(
    null,
  );
  const [notesDraft, setNotesDraft] = useState<string | null>(null);

  if (sessionQ.isLoading) {
    return <div style={{ padding: 24, color: 'var(--ink-3)' }}>Loading workout…</div>;
  }
  if (!sessionQ.data) {
    return (
      <div style={{ padding: 24, color: 'var(--ink-3)' }}>
        Workout not found.
      </div>
    );
  }

  const session = sessionQ.data.workout;
  const sets = sessionQ.data.sets;
  const exercises = exercisesQ.data?.exercises ?? [];

  // Group sets by exercise (preserve order via min setIndex).
  const grouped = groupSetsByExercise(sets, exercises);

  // Order by block.
  const byBlock = BLOCK_ORDER.map((b) => ({
    block: b,
    items: grouped.filter((g) => (g.block as ExerciseBlock) === b),
  }));

  const totalSets = sets.length;
  const doneSets = sets.filter((s) => s.status === 'completed').length;
  const skippedCount = grouped.filter((g) => g.allSkipped).length;
  // Active elapsed excludes paused time (see health-workout-pause design).
  // While paused the clock freezes at pausedAt; the banner explains why.
  const paused = session.pausedAt !== null;
  const elapsedLabel = formatActiveElapsed(
    session.startTime,
    session.pausedAt,
    session.pausedTotalS,
  );

  function changeSet(set: ExerciseSetDto, patch: Partial<ExerciseSetDto>) {
    patchSet.mutate({ sessionId, setId: set.id, patch });
  }

  function addSetTo(exerciseId: string | null, exerciseName: string, block: string | null) {
    const nextIndex = sets.length + 1;
    addSet.mutate({
      sessionId,
      input: {
        exerciseId,
        exerciseName,
        block,
        setIndex: nextIndex,
        status: 'planned',
      },
    });
  }

  function addExerciseToWorkout(ex: ExerciseDto, block: ExerciseBlock) {
    const isSetBased = SET_BASED.includes(ex.type);
    const nextIndex = sets.length + 1;
    if (isSetBased) {
      const nSets = parseSetsFromPrescription(ex.prescription) ?? 3;
      for (let i = 0; i < nSets; i++) {
        addSet.mutate({
          sessionId,
          input: {
            exerciseId: ex.id,
            exerciseName: ex.name,
            block,
            setIndex: nextIndex + i,
            prescription: ex.prescription,
            status: 'planned',
          },
        });
      }
    } else {
      addSet.mutate({
        sessionId,
        input: {
          exerciseId: ex.id,
          exerciseName: ex.name,
          block,
          setIndex: nextIndex,
          prescription: ex.prescription,
          status: 'planned',
        },
      });
    }
    setPicker(null);
  }

  function removeExercise(group: GroupedExercise) {
    if (!confirm(`Remove ${group.exerciseName} from this workout?`)) return;
    for (const s of group.sets) {
      deleteSet.mutate({ sessionId, setId: s.id });
    }
  }

  function startWorkout() {
    // Planned → in_progress + stamp startTime. The tracker re-renders with
    // the running clock, exposing the Complete button as the next step.
    patchWorkout.mutate({
      id: sessionId,
      patch: {
        status: 'in_progress',
        startTime: new Date().toISOString(),
      },
    });
  }

  function completeWorkout() {
    const endTime = new Date();
    // If the user somehow hit Complete without first starting (rare — but
    // possible from a planned session before the Start button landed), stamp
    // startTime to match endTime so the server-derived duration is 0 instead
    // of a confusing 1700000+ minute value from a null startTime.
    const startTimeIso = session.startTime ?? endTime.toISOString();
    // Deliberately OMIT durationMin: the server (updateWorkoutSession) holds
    // the AUTHORITATIVE pause fields and derives duration from active elapsed
    // (end − start − pausedTotalS). Pinning a locally-computed duration here
    // could disagree with the server's pause state if the session was
    // paused/resumed elsewhere. The mutation invalidates and the tracker
    // re-renders the server's value.
    patchWorkout.mutate({
      id: sessionId,
      patch: {
        status: 'completed',
        ...(session.startTime ? {} : { startTime: startTimeIso }),
        endTime: endTime.toISOString(),
        notes: notesDraft ?? session.notes,
      },
    });
  }

  function pause() {
    pauseWorkout.mutate(sessionId);
  }
  function resume() {
    resumeWorkout.mutate(sessionId);
  }

  return (
    <div className={styles.trackerGrid}>
      <div>
        {/* Header */}
        <div className={styles.headerCard}>
          <div className={styles.headerTitleRow}>
            <h3 className={styles.headerName}>{session.name}</h3>
            {session.type && <TypeChip type={session.type as ExerciseDto['type']} />}
            {editMode && <span className={styles.editBadge}>Editing</span>}
            <div className={styles.headerMeta}>
              {!editMode ? (
                <>
                  {session.startTime ? (
                    <span>
                      Started {new Date(session.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} · {paused ? '⏸ Paused' : `${elapsedLabel} elapsed`}
                    </span>
                  ) : (
                    <span>Not started</span>
                  )}
                  {/* Planned session → primary Start button. Stamps startTime
                      so elapsed time + duration logging work for real. The
                      Complete button is gated below until the session has a
                      startTime so a "complete without start" can't happen by
                      accident from the regular UI. */}
                  {session.status === 'planned' && (
                    <button
                      type="button"
                      className="kos-btn kos-btn-primary"
                      onClick={startWorkout}
                      disabled={patchWorkout.isPending}
                    >
                      <Icon name="check" size={11} />{' '}
                      {patchWorkout.isPending ? 'Starting…' : 'Start workout'}
                    </button>
                  )}
                  {/* Pause while running / Resume while paused — paused keeps
                      status='in_progress'; time stops counting (see design). */}
                  {session.status === 'in_progress' && !paused && (
                    <button
                      type="button"
                      className="kos-btn"
                      onClick={pause}
                      disabled={pauseWorkout.isPending}
                    >
                      <Icon name="pause" size={11} />{' '}
                      {pauseWorkout.isPending ? 'Pausing…' : 'Pause'}
                    </button>
                  )}
                  {session.status === 'in_progress' && paused && (
                    <button
                      type="button"
                      className="kos-btn kos-btn-primary"
                      onClick={resume}
                      disabled={resumeWorkout.isPending}
                    >
                      <Icon name="play" size={11} />{' '}
                      {resumeWorkout.isPending ? 'Resuming…' : 'Resume'}
                    </button>
                  )}
                  <button
                    type="button"
                    className="kos-btn"
                    onClick={() => setEditMode(true)}
                  >
                    <Icon name="plus" size={11} /> Edit
                  </button>
                </>
              ) : (
                <>
                  <span style={{ color: 'var(--accent)' }}>
                    Add, swap, or remove exercises.
                  </span>
                  <button
                    type="button"
                    className="kos-btn kos-btn-primary"
                    onClick={() => setEditMode(false)}
                  >
                    <Icon name="check" size={11} /> Done editing
                  </button>
                </>
              )}
            </div>
          </div>
          <div className={styles.headerSub}>
            {session.date}
            {session.status === 'in_progress' ? ' · in progress' : session.status === 'completed' ? ' · completed' : ''}
          </div>
          {paused && (
            <div className={styles.pausedBanner}>
              <Icon name="pause" size={11} /> Paused — time not counting
            </div>
          )}
          {/* Surface pause/resume failures (e.g. a double-click 409 or a
              stale-tab race) instead of silently reverting the button. The
              mutations invalidate on settled so the UI self-corrects; the
              message just explains it. TanStack resets the error state on the
              next mutate, so a successful action clears this automatically. */}
          {(pauseWorkout.isError || resumeWorkout.isError) && (
            <div className={styles.error}>
              Couldn't update — the workout may have changed elsewhere. It
              refreshes automatically.
            </div>
          )}
        </div>

        {/* Blocks */}
        {byBlock.map((g) => (
          <div key={g.block} className={styles.block}>
            <div className={styles.blockHead}>
              <div>
                <div className={styles.blockHeadName}>
                  {EXERCISE_BLOCK_META[g.block].name}
                </div>
                <div className={styles.blockHeadDesc}>
                  {EXERCISE_BLOCK_META[g.block].desc}
                </div>
              </div>
              <span
                style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}
              >
                <span className={styles.blockHeadCount}>
                  {g.items.length} exercise{g.items.length !== 1 ? 's' : ''}
                </span>
                {editMode && (
                  <button
                    type="button"
                    className="kos-btn"
                    onClick={() => setPicker({ mode: 'add', block: g.block })}
                  >
                    <Icon name="plus" size={11} /> Add
                  </button>
                )}
              </span>
            </div>

            {g.items.map((group) => (
              <ExerciseGroup
                key={group.exerciseName + (group.exerciseId ?? '')}
                group={group}
                exercises={exercises}
                editMode={editMode}
                onSetChange={changeSet}
                onAddSet={() =>
                  addSetTo(group.exerciseId, group.exerciseName, group.block)
                }
                onSkipExercise={() =>
                  setSkipPrompt({
                    exerciseId: group.exerciseId,
                    name: group.exerciseName,
                  })
                }
                onRemove={() => removeExercise(group)}
                onSwap={() =>
                  setPicker({
                    mode: 'swap',
                    block: (group.block as ExerciseBlock) ?? 'main',
                    swapExerciseId: group.exerciseId ?? undefined,
                  })
                }
                onOpenDetail={setDetailExercise}
              />
            ))}

            {g.items.length === 0 && editMode && (
              <div
                className={styles.blockEmpty}
                onClick={() => setPicker({ mode: 'add', block: g.block })}
              >
                <Icon name="plus" size={11} /> Add to {EXERCISE_BLOCK_META[g.block].name}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Sidebar */}
      <div>
        <div className={styles.progressCard}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
            Session progress
          </div>
          <div className={styles.progressBigRow}>
            <span className={styles.progressBigNum}>
              {doneSets} / {totalSets || 1}
            </span>
            <span className={styles.progressBigLabel}>sets done</span>
          </div>
          <div className={styles.progressBar}>
            <div
              className={styles.progressBarFill}
              style={{ width: `${(doneSets / Math.max(1, totalSets)) * 100}%` }}
            />
          </div>
          <div className={styles.kpiPair}>
            <div>
              <span>Elapsed</span>
              <span>{paused ? '⏸ Paused' : elapsedLabel}</span>
            </div>
            <div>
              <span>Skipped</span>
              <span>{skippedCount}</span>
            </div>
          </div>
        </div>

        <div className={styles.sideCard}>
          <div className={styles.sideCardHead}>
            <Icon name="sparkles" size={11} />
            <span>Agent notes</span>
          </div>
          <div className={styles.sideCardBody}>
            {session.status === 'completed'
              ? 'Workout complete. The Fitness Agent will review the data and post notes here for next time.'
              : 'Notes from the Fitness Agent appear here after the workout — based on RPE, load progression, and recovery.'}
          </div>
        </div>

        <div className={styles.sideCard}>
          <div className={styles.sideCardHead}>Session notes</div>
          <textarea
            className={styles.sideTextarea}
            value={notesDraft ?? session.notes ?? ''}
            onChange={(e) => setNotesDraft(e.target.value)}
            placeholder="How did it feel? Anything to flag?"
            onBlur={() => {
              if (notesDraft !== null && notesDraft !== session.notes) {
                patchWorkout.mutate({ id: sessionId, patch: { notes: notesDraft } });
              }
            }}
          />
        </div>

        {/* Only offer Complete once the session is actually running. While
            it's still `planned`, the header's Start button is the only way
            to advance — keeps startTime / endTime / durationMin honest. */}
        {session.status === 'in_progress' && (
          <button
            type="button"
            className={`kos-btn kos-btn-primary ${styles.completeBtn}`}
            onClick={completeWorkout}
            disabled={patchWorkout.isPending}
          >
            <Icon name="check" size={12} /> Complete workout
          </button>
        )}
      </div>

      {picker && (
        <ExercisePicker
          exercises={exercises}
          mode={picker.mode}
          defaultBlock={picker.block}
          onPick={(ex) => {
            if (picker.mode === 'add' && picker.block) {
              addExerciseToWorkout(ex, picker.block);
            } else if (picker.mode === 'swap' && picker.swapExerciseId) {
              // Replace all sets of swapExerciseId with the new exercise.
              const oldGroup = grouped.find((g) => g.exerciseId === picker.swapExerciseId);
              if (oldGroup) {
                for (const s of oldGroup.sets) {
                  patchSet.mutate({
                    sessionId,
                    setId: s.id,
                    patch: {
                      exerciseId: ex.id,
                      exerciseName: ex.name,
                      prescription: ex.prescription,
                      status: 'planned',
                      weight: null,
                      reps: null,
                      difficulty: null,
                    },
                  });
                }
              }
              setPicker(null);
            }
          }}
          onClose={() => setPicker(null)}
        />
      )}

      {skipPrompt && (
        <SkipReasonModal
          exerciseName={skipPrompt.name}
          onClose={() => setSkipPrompt(null)}
          onConfirm={(reason) => {
            // Mark every set in the group as skipped with the reason.
            const target = grouped.find(
              (g) =>
                g.exerciseId === skipPrompt.exerciseId &&
                g.exerciseName === skipPrompt.name,
            );
            if (target) {
              for (const s of target.sets) {
                patchSet.mutate({
                  sessionId,
                  setId: s.id,
                  patch: { status: 'skipped', skipReason: reason },
                });
              }
            }
            setSkipPrompt(null);
          }}
        />
      )}

      {/* Exercise detail modal — view only (no edit/add-to-workout in tracker context) */}
      {detailExercise && (
        <ExerciseDetailModal
          ex={detailExercise}
          equipment={equipmentQ.data ?? []}
          fav={favIdsQ.data?.includes(detailExercise.id) ?? false}
          onToggleFav={(ex) =>
            toggleFav.mutate({ id: ex.id, favored: favIdsQ.data?.includes(ex.id) ?? false })
          }
          onClose={() => setDetailExercise(null)}
        />
      )}
    </div>
  );
}

// ─── Exercise group component ───────────────────────────────────────────

interface GroupedExercise {
  exerciseId: string | null;
  exerciseName: string;
  block: string | null;
  prescription: string | null;
  sets: ExerciseSetDto[];
  allCompleted: boolean;
  allSkipped: boolean;
  anyCompleted: boolean;
}

function groupSetsByExercise(sets: ExerciseSetDto[], _exercises: ExerciseDto[]): GroupedExercise[] {
  const byKey = new Map<string, GroupedExercise>();
  for (const s of sets.slice().sort((a, b) => a.setIndex - b.setIndex)) {
    const key = `${s.exerciseId ?? ''}::${s.exerciseName}::${s.block ?? ''}`;
    let g = byKey.get(key);
    if (!g) {
      g = {
        exerciseId: s.exerciseId,
        exerciseName: s.exerciseName,
        block: s.block,
        prescription: s.prescription,
        sets: [],
        allCompleted: false,
        allSkipped: false,
        anyCompleted: false,
      };
      byKey.set(key, g);
    }
    g.sets.push(s);
  }
  for (const g of byKey.values()) {
    g.allCompleted = g.sets.every((s) => s.status === 'completed');
    g.allSkipped = g.sets.every((s) => s.status === 'skipped');
    g.anyCompleted = g.sets.some((s) => s.status === 'completed');
  }
  return [...byKey.values()];
}

function ExerciseGroup({
  group,
  exercises,
  editMode,
  onSetChange,
  onAddSet,
  onSkipExercise,
  onRemove,
  onSwap,
  onOpenDetail,
}: {
  group: GroupedExercise;
  exercises: ExerciseDto[];
  editMode: boolean;
  onSetChange: (set: ExerciseSetDto, patch: Partial<ExerciseSetDto>) => void;
  onAddSet: () => void;
  onSkipExercise: () => void;
  onRemove: () => void;
  onSwap: () => void;
  onOpenDetail: (ex: ExerciseDto) => void;
}) {
  const lib = useMemo(
    () => exercises.find((e) => e.id === group.exerciseId),
    [exercises, group.exerciseId],
  );
  const type = (lib?.type ?? 'strength') as ExerciseDto['type'];
  const isSetBased = SET_BASED.includes(type);

  const completed = group.allCompleted;
  const skipped = group.allSkipped;

  return (
    <div
      className={`${styles.ex} ${completed ? styles.exCompleted : ''} ${skipped ? styles.exSkipped : ''}`}
    >
      <div className={styles.exHead}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span className={styles.exName}>{group.exerciseName}</span>
            {lib && <TypeChip type={lib.type} />}
            {lib && (
              <button
                type="button"
                className="kos-btn"
                style={{ fontSize: 12 }}
                onClick={() => onOpenDetail(lib)}
              >
                Details
              </button>
            )}
          </div>
          <div className={styles.exMeta}>
            {group.prescription ?? lib?.prescription ?? '—'}
            {lib && lib.muscles.length > 0 && ` · ${lib.muscles.join(' · ')}`}
          </div>
        </div>

        <div className={styles.exActions}>
          {(completed || skipped) && <StatusPill status={completed ? 'completed' : 'skipped'} />}
          {!completed && !skipped && (
            <button type="button" className="kos-btn" onClick={onSkipExercise}>
              <Icon name="x" size={11} /> Skip
            </button>
          )}
          {editMode && (
            <>
              <button
                type="button"
                className="kos-btn"
                title="Swap exercise"
                onClick={onSwap}
              >
                <Icon name="arrowRight" size={11} />
              </button>
              <button
                type="button"
                className="kos-btn"
                title="Remove exercise"
                onClick={onRemove}
                style={{ color: 'var(--neg)' }}
              >
                <Icon name="x" size={11} />
              </button>
            </>
          )}
        </div>
      </div>

      {!skipped && isSetBased && (
        <div>
          <div className={styles.setHead}>
            <span>Set</span>
            <span>Prev</span>
            <span>Weight</span>
            <span>Reps</span>
            <span></span>
          </div>
          {group.sets.map((s) => (
            <SetRow key={s.id} set={s} onChange={(patch) => onSetChange(s, patch)} />
          ))}
          {editMode && (
            <button type="button" className={styles.addSet} onClick={onAddSet}>
              <Icon name="plus" size={10} /> Add set
            </button>
          )}
        </div>
      )}

      {!skipped && !isSetBased &&
        group.sets.map((s) => (
          <SingleEffortRow key={s.id} set={s} onChange={(patch) => onSetChange(s, patch)} />
        ))}
    </div>
  );
}

// ─── Set row (strength etc.) ────────────────────────────────────────────

function SetRow({
  set,
  onChange,
}: {
  set: ExerciseSetDto;
  onChange: (patch: Partial<ExerciseSetDto>) => void;
}) {
  const completed = set.status === 'completed';
  const skipped = set.status === 'skipped';
  const hasDiff = completed && set.difficulty != null;
  const needsDiff = completed && set.difficulty == null;

  const prev = formatPreviousSet(set);
  const prevColor = set.prevDifficulty != null ? diffColor(set.prevDifficulty) : null;

  // Local draft for the numeric inputs so the user can type freely without
  // a mutation firing on every keystroke. Commit happens on blur or Enter.
  // When the server value changes (e.g. another tab edited), the effect
  // below syncs the draft so we don't get stale values stuck in the UI.
  const weightServer = set.weight != null ? String(set.weight) : '';
  const repsServer = set.reps != null ? String(set.reps) : '';
  const [weightDraft, setWeightDraft] = useState(weightServer);
  const [repsDraft, setRepsDraft] = useState(repsServer);
  useEffect(() => setWeightDraft(weightServer), [weightServer]);
  useEffect(() => setRepsDraft(repsServer), [repsServer]);

  function commitWeight(): void {
    if (weightDraft === weightServer) return;
    const next = weightDraft === '' ? null : Number(weightDraft);
    if (next !== null && Number.isNaN(next)) {
      setWeightDraft(weightServer); // bad input — revert
      return;
    }
    onChange({ weight: next });
  }
  function commitReps(): void {
    if (repsDraft === repsServer) return;
    const next = repsDraft === '' ? null : Number(repsDraft);
    if (next !== null && Number.isNaN(next)) {
      setRepsDraft(repsServer);
      return;
    }
    onChange({ reps: next });
  }
  function handleEnter(commit: () => void) {
    return (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        commit();
        (e.target as HTMLInputElement).blur();
      }
    };
  }

  return (
    <>
      <div className={`${styles.setRow} ${skipped ? styles.setSkippedRow : ''}`}>
        <span className={styles.setNum}>{set.setIndex}</span>
        <span
          className={styles.setPrev}
          style={prevColor ? { color: prevColor, borderColor: prevColor } : undefined}
          title={set.prevDate ? `Previous on ${set.prevDate}` : undefined}
        >
          <span>{prev}</span>
          {set.prevDate && <span className={styles.setPrevDate}>{formatPrevDate(set.prevDate)}</span>}
        </span>
        <input
          type="number"
          inputMode="decimal"
          className={styles.setInput}
          placeholder="kg"
          value={weightDraft}
          onChange={(e) => setWeightDraft(e.target.value)}
          onBlur={commitWeight}
          onKeyDown={handleEnter(commitWeight)}
          disabled={skipped}
        />
        <input
          type="number"
          inputMode="numeric"
          className={styles.setInput}
          placeholder="reps"
          value={repsDraft}
          onChange={(e) => setRepsDraft(e.target.value)}
          onBlur={commitReps}
          onKeyDown={handleEnter(commitReps)}
          disabled={skipped}
        />
        <span className={styles.setActions}>
          {!completed && !skipped && (
            <>
              <button
                type="button"
                title="Mark done"
                className={`${styles.setActBtn} ${styles.setActDone}`}
                onClick={() => onChange({ status: 'completed' })}
              >
                <Icon name="check" size={12} />
              </button>
              <button
                type="button"
                title="Skip set"
                className={`${styles.setActBtn} ${styles.setActSkip}`}
                onClick={() => onChange({ status: 'skipped' })}
              >
                <Icon name="x" size={12} />
              </button>
            </>
          )}
          {needsDiff && (
            <button
              type="button"
              title="Undo"
              className={styles.setActBtn}
              onClick={() => onChange({ status: 'planned', difficulty: null })}
            >
              <Icon name="check" size={12} />
            </button>
          )}
          {hasDiff && (
            <button
              type="button"
              className={styles.diffPill}
              style={{
                background: diffColor(set.difficulty!),
                borderColor: diffColor(set.difficulty!),
              }}
              onClick={() => onChange({ status: 'planned', difficulty: null })}
              title="Undo"
            >
              {set.difficulty}
            </button>
          )}
          {skipped && (
            <button
              type="button"
              className={styles.setActBtn}
              onClick={() => onChange({ status: 'planned', skipReason: null })}
              title="Restore"
            >
              <Icon name="x" size={12} />
            </button>
          )}
        </span>
      </div>
      {needsDiff && (
        <div className={styles.setDiffPrompt}>
          <span className={styles.setDiffLabel}>How hard was that?</span>
          <DifficultyPicker value={null} onPick={(v) => onChange({ difficulty: v })} />
        </div>
      )}
    </>
  );
}

// ─── Single-effort row (cardio etc.) ────────────────────────────────────

function SingleEffortRow({
  set,
  onChange,
}: {
  set: ExerciseSetDto;
  onChange: (patch: Partial<ExerciseSetDto>) => void;
}) {
  const completed = set.status === 'completed';
  const skipped = set.status === 'skipped';
  const hasDiff = completed && set.difficulty != null;
  const needsDiff = completed && set.difficulty == null;

  // Local draft for the free-text "actual effort" input. Commit on blur or
  // Enter, same as the numeric inputs on SetRow — keeps the typing
  // experience snappy and avoids one mutation per keystroke.
  const actualServer = set.actual ?? '';
  const [actualDraft, setActualDraft] = useState(actualServer);
  useEffect(() => setActualDraft(actualServer), [actualServer]);
  const prevColor = set.prevDifficulty != null ? diffColor(set.prevDifficulty) : null;

  function commitActual(): void {
    if (actualDraft === actualServer) return;
    onChange({ actual: actualDraft || null });
  }

  return (
    <div className={styles.singleEffort}>
      {(set.prevActual || set.prevDate) && (
        <div
          className={styles.singlePrev}
          style={prevColor ? { color: prevColor, borderColor: prevColor } : undefined}
        >
          <span>{set.prevActual ?? 'Previous effort'}</span>
          {set.prevDate && <span>{formatPrevDate(set.prevDate)}</span>}
        </div>
      )}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <input
          className={styles.singleEffortInput}
          placeholder="What did you actually do? (e.g. 5.2km · 32:00 · 6:10/km)"
          value={actualDraft}
          onChange={(e) => setActualDraft(e.target.value)}
          onBlur={commitActual}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commitActual();
              (e.target as HTMLInputElement).blur();
            }
          }}
          disabled={skipped}
          style={{ flex: 1 }}
        />
        {!completed && !skipped && (
          <>
            <button
              type="button"
              title="Done"
              className={`${styles.setActBtn} ${styles.setActDone}`}
              onClick={() => onChange({ status: 'completed' })}
            >
              <Icon name="check" size={12} />
            </button>
          </>
        )}
        {needsDiff && (
          <button
            type="button"
            className={styles.setActBtn}
            onClick={() => onChange({ status: 'planned', difficulty: null })}
            title="Undo"
          >
            <Icon name="check" size={12} />
          </button>
        )}
        {hasDiff && (
          <button
            type="button"
            className={styles.diffPill}
            style={{
              background: diffColor(set.difficulty!),
              borderColor: diffColor(set.difficulty!),
            }}
            onClick={() => onChange({ status: 'planned', difficulty: null })}
            title="Undo"
          >
            {set.difficulty}
          </button>
        )}
      </div>
      {needsDiff && (
        <div className={styles.setDiffPrompt}>
          <span className={styles.setDiffLabel}>How hard was that?</span>
          <DifficultyPicker value={null} onPick={(v) => onChange({ difficulty: v })} />
        </div>
      )}
    </div>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────

function parseSetsFromPrescription(prescription: string | null | undefined): number | null {
  if (!prescription) return null;
  const m = prescription.match(/^(\d+)\s*[×x]\s*\d+/);
  return m && m[1] ? Number(m[1]) : null;
}

function diffColor(v: number): string {
  return (
    [
      '#5a7a4a',
      '#7a8a5a',
      '#a8843a',
      '#b8714a',
      '#b85544',
    ][v - 1] ?? '#888'
  );
}

function formatPreviousSet(set: ExerciseSetDto): string {
  if (set.prevWeight != null && set.prevReps != null) return `${set.prevWeight}×${set.prevReps}`;
  if (set.prevReps != null) return `${set.prevReps} reps`;
  if (set.prevActual) return set.prevActual;
  return '—';
}

function formatPrevDate(value: string): string {
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString([], { day: '2-digit', month: 'short' });
}

// Re-export for parent module convenience.
export type { WorkoutSessionDto };
